import React, { FC, MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CallRecIntegration } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    callRecIntegrationConfigUrl,
    testCallRecIntegrationResultURL,
    testCallRecIntegrationURL
} from '../../../NonReact/constants/ApiConstants'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { requestJSONAsync } from '../../Common/API/AsyncFetching'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import FormRow from '../../Common/Components/FormRow'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import Icon, { IconName } from '../../Common/Components/Icon'
import Loader from '../../Common/Components/Loader'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { ReactState } from '../../Common/Contexts'
import { displayError } from '../../Common/Utils/Messages'
import { encrypt } from '../../Common/Utils/Security'
import {
    DEFAULT_INTEGRATION_TYPE,
    HASHED_PASSWORD_STRING,
    INTEGRATION_TYPE_DEFAULT_VALUE
} from '../Constants/Constants'
import {
    ACCOUNT_AUTH_PASSWORD_NAME,
    ACCOUNT_AUTH_USERNAME_NAME,
    DUBBER_API_PARAMETERS_ONE_NAME,
    DUBBER_API_PARAMETERS_TWO_NAME,
    DUBBER_LOCATION_URLS_NAME,
    INTEGRATION_GENERAL_NAME,
    MAIN_DEVELOPER_AUTHENTICATION_DETAILS_PASSWORD_NAME,
    MAIN_DEVELOPER_AUTHENTICATION_DETAILS_USERNAME_NAME,
    PARTITION_SCOPE_NAME,
    TELEPHONY_SERVER_SCOPE_NAME
} from '../Constants/FieldNames'
import { CallRecIntegrationResponse, DubberLocationUrls, TestIntegrationRequest } from '../Types/Types'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { OutlinedPasswordTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedPasswordTextInput'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { OutlinedSelectInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedSelectInput'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { StatusCodes } from '../../../NonReact/constants/StatusCodes'

type Props = {
    viewOnly: boolean | undefined
    selectedCallRecIntegration?: Nullable<CallRecIntegrationResponse>
    sessionNonce: Definable<string>
}

export const dubberLocationUrls: DubberLocationUrls[] = [
    {
        renderElement: <div>api.dubber.net/sandbox</div>,
        value: 'api.dubber.net/sandbox'
    },
    {
        renderElement: <div>api.dubber.net/uk1</div>,
        value: 'api.dubber.net/uk1'
    },
    {
        renderElement: <div>api.dubber.net/uk</div>,
        value: 'api.dubber.net/uk'
    },
    {
        renderElement: <div>api.dubber.net/eu</div>,
        value: 'api.dubber.net/eu'
    },
    {
        renderElement: <div>api.dubber.net/us</div>,
        value: 'api.dubber.net/us'
    },
    {
        renderElement: <div>api.dubber.net/au</div>,
        value: 'api.dubber.net/au'
    },
    {
        renderElement: <div>api.dubber.net/jp</div>,
        value: 'api.dubber.net/jp'
    },
    {
        renderElement: <div>api.dubber.net/sg</div>,
        value: 'api.dubber.net/sg'
    }
]

export const DubberCommunicationsSettings: FC<Props> = ({
    viewOnly,
    selectedCallRecIntegration,
    sessionNonce
}: Props): ReactElement => {
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const isInitialMount: MutableRefObject<boolean> = useRef(true)

    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(CallRecIntegration.NAME)

    const [isTestingIntegration, setIsTestingIntegration]: ReactState<Definable<boolean>> =
        useState<Definable<boolean>>(false)
    const [testingIntegrationError, setTestingIntegrationError]: ReactState<
        Definable<{
            error: boolean
            message: string
        }>
    > = useState<Definable<{ error: boolean; message: string }>>()

    const { watch, setValue, getValues }: UseFormMethods = useFormContext()

    const dubberLocationUrlValue: string = watch(DUBBER_LOCATION_URLS_NAME)
    const telSysID: string = watch(TELEPHONY_SERVER_SCOPE_NAME)
    const partitionID: number = watch(PARTITION_SCOPE_NAME)

    const CallRecIntType: number = watch(INTEGRATION_GENERAL_NAME)
    const AuthUserID1: string = watch(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_USERNAME_NAME)
    const AuthUserID2: string = watch(ACCOUNT_AUTH_USERNAME_NAME)
    const AuthPassword1: string = watch(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_PASSWORD_NAME)
    const AuthPassword2: string = watch(ACCOUNT_AUTH_PASSWORD_NAME)
    const ParameterText1: string = watch(DUBBER_API_PARAMETERS_ONE_NAME)
    const ParameterText2: string = watch(DUBBER_API_PARAMETERS_TWO_NAME)

    useEffect((): void => {
        if (isInitialMount.current) {
            isInitialMount.current = false
            if (
                isVarNotEmpty(selectedCallRecIntegration) &&
                selectedCallRecIntegration?.APICommunicationSettings?.AuthPassword1Provided
            )
                return
        }

        if (
            isVarNotEmpty(dubberLocationUrlValue) &&
            dubberLocationUrls.some((url: DubberLocationUrls): boolean => url.value === dubberLocationUrlValue)
        ) {
            // Capture the location and current credentials at request time so a late
            // response cannot overwrite a newer selection or values the user has since
            // entered. The fetched username/password are obscured placeholders (they
            // contain the "*****" hidden marker) that the backend swaps for the
            // location's default account, so clobbering entered credentials with them
            // breaks authentication even when the correct ones were typed (APD-13064).
            const requestedLocation: string = dubberLocationUrlValue
            const usernameBeforeFetch: string = getValues(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_USERNAME_NAME)
            const passwordBeforeFetch: string = getValues(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_PASSWORD_NAME)

            requestJSON<TestIntegrationRequest>(
                callRecIntegrationConfigUrl(
                    telSysID,
                    partitionID,
                    DEFAULT_INTEGRATION_TYPE,
                    dubberLocationUrls.find(
                        (url: { renderElement: ReactElement; value: string }): boolean =>
                            url.value === dubberLocationUrlValue
                    )?.value ?? EMPTY_STRING
                ),
                {
                    method: RESTMethods.GET
                }
            )
                .then((res: JSONResponse<TestIntegrationRequest>): void => {
                    if (!isMounted.current) return

                    const locationChanged: boolean = getValues(DUBBER_LOCATION_URLS_NAME) !== requestedLocation
                    const credentialsEdited: boolean =
                        getValues(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_USERNAME_NAME) !== usernameBeforeFetch ||
                        getValues(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_PASSWORD_NAME) !== passwordBeforeFetch

                    // Stale response, or the user is already entering their own credentials.
                    if (locationChanged || credentialsEdited) return

                    setValue(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_USERNAME_NAME, res?.AuthUserID1)
                    setValue(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_PASSWORD_NAME, HASHED_PASSWORD_STRING)
                })
                .catch(displayError)
        } else if (isMounted.current) {
            setValue(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_USERNAME_NAME, EMPTY_STRING)
            setValue(MAIN_DEVELOPER_AUTHENTICATION_DETAILS_PASSWORD_NAME, EMPTY_STRING)
        }
    }, [dubberLocationUrlValue])

    const testIntegratrionPayloadMapper = (): TestIntegrationRequest => {
        const payload: TestIntegrationRequest = {
            TypeID: CallRecIntType,
            IPAddress1: dubberLocationUrlValue,
            AuthUserID1,
            AuthPassword1:
                AuthPassword1 === HASHED_PASSWORD_STRING
                    ? HASHED_PASSWORD_STRING
                    : encrypt(AuthPassword1, sessionNonce),
            AuthUserID2,
            AuthPassword2:
                AuthPassword2 === HASHED_PASSWORD_STRING
                    ? HASHED_PASSWORD_STRING
                    : encrypt(AuthPassword2, sessionNonce),
            ParameterText1,
            ParameterText2,
            UniqueID: IsVarEmpty(selectedCallRecIntegration)
                ? INTEGRATION_TYPE_DEFAULT_VALUE
                : selectedCallRecIntegration.CallRecIntGUID
        }

        return payload
    }

    const testCommunicationIntegration = (): void => {
        setIsTestingIntegration(true)
        requestJSONAsync(testCallRecIntegrationURL(`${telSysID}`, `${partitionID}`), testCallRecIntegrationResultURL, {
            method: RESTMethods.POST,
            body: testIntegratrionPayloadMapper(),
            timingOptions: {
                maxChecks: 15,
                frequency: 1000,
                errorMessage: language.get(CallRecIntegration.Error_TestIntegration_Timeout)
            },
            additionalAllowableNoContentCodes: [StatusCodes.OK]
        })
            .then((): void => {
                if (isMounted.current) {
                    setIsTestingIntegration(false)
                    setTestingIntegrationError({
                        message: language.get(CallRecIntegration.DubberSuccessfullIntegration),
                        error: false
                    })
                }
            })
            .catch((err: Error): void => {
                if (isMounted.current) {
                    setIsTestingIntegration(false)
                    setTestingIntegrationError({ message: err.message, error: true })
                }
            })
    }

    return (
        <div className='dubber-communications-tab tab-area'>
            <FormRow>
                <OutlinedSelectInput
                    name={DUBBER_LOCATION_URLS_NAME}
                    labelLeft={language.get(CallRecIntegration.DubberApiComSettingsLocationUrls)}
                    items={dubberLocationUrls}
                    defaultValue={String(dubberLocationUrls[0].value)}
                    disabled={viewOnly}
                    rules={{ required: true }}
                />
            </FormRow>
            <Heading level={HeadingLevel.ONE}>{language.get(CallRecIntegration.DubberComSettingsDevAuthTitle)}</Heading>
            <FormRow>
                <OutlinedTextInput
                    labelLeft={language.get(CallRecIntegration.ApiComSettingsAuthDetailsMainUserName)}
                    inputName={MAIN_DEVELOPER_AUTHENTICATION_DETAILS_USERNAME_NAME}
                    disabled={viewOnly}
                />
            </FormRow>
            <FormRow>
                <OutlinedPasswordTextInput
                    inputName={MAIN_DEVELOPER_AUTHENTICATION_DETAILS_PASSWORD_NAME}
                    labelLeft={language.get(CallRecIntegration.DubberComSettingsDevAuthUserPassword)}
                    disabled={viewOnly}
                    isPasswordCheckerEnabled={false}
                    isRandomiserEnabled={false}
                    isShowPasswordEnabled={false}
                />
            </FormRow>
            <Heading level={HeadingLevel.ONE}>
                {language.get(CallRecIntegration.DubberComSettingsAccountApiAuthTitle)}
            </Heading>
            <FormRow>
                <ValidationErrors name={ACCOUNT_AUTH_USERNAME_NAME}>
                    <OutlinedTextInput
                        labelLeft={language.get(CallRecIntegration.ApiComSettingsAuthDetailsSecondaryUserName)}
                        inputName={ACCOUNT_AUTH_USERNAME_NAME}
                        disabled={viewOnly}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <OutlinedPasswordTextInput
                    inputName={ACCOUNT_AUTH_PASSWORD_NAME}
                    labelLeft={language.get(CallRecIntegration.DubberComSettingsAccountApiAuthUserPassword)}
                    disabled={viewOnly}
                    isPasswordCheckerEnabled={false}
                    isRandomiserEnabled={false}
                    isShowPasswordEnabled={false}
                />
            </FormRow>

            <Heading level={HeadingLevel.ONE}>{language.get(CallRecIntegration.DubberComSettings_Parameters)}</Heading>
            <FormRow>
                <ValidationErrors name={DUBBER_API_PARAMETERS_ONE_NAME}>
                    <OutlinedTextInput
                        labelLeft={language.get(CallRecIntegration.DubberComSettingsApiParamOne)}
                        inputName={DUBBER_API_PARAMETERS_ONE_NAME}
                        disabled={viewOnly}
                    />
                </ValidationErrors>
            </FormRow>
            <FormRow>
                <ValidationErrors name={DUBBER_API_PARAMETERS_TWO_NAME}>
                    <OutlinedTextInput
                        labelLeft={language.get(CallRecIntegration.DubberComSettingsApiParamTWO)}
                        inputName={DUBBER_API_PARAMETERS_TWO_NAME}
                        disabled={viewOnly}
                    />
                </ValidationErrors>
            </FormRow>
            <Heading level={HeadingLevel.ONE}>
                {language.get(CallRecIntegration.DubberComSettings_TestIntegrationAuth)}
            </Heading>
            <FormRow>
                <RoundedButton
                    className={`${
                        isTestingIntegration ? 'test-integration-button is-generating' : 'test-integration-button'
                    }`}
                    type='button'
                    onClick={testCommunicationIntegration}
                    shrink
                >
                    {isTestingIntegration ? <Loader className='generating-token-loader' /> : 'Test'}
                </RoundedButton>
                {testingIntegrationError ? (
                    <div
                        className={`test-integration-result ${
                            testingIntegrationError.error ? 'test-integration-error' : 'test-integration-success'
                        }`}
                    >
                        <Icon iconName={testingIntegrationError.error ? IconName.X : IconName.SUCCESS} />
                        <div className='lc-message'>{testingIntegrationError.message}</div>
                    </div>
                ) : (
                    <></>
                )}
            </FormRow>
        </div>
    )
}
